// HR Oversight Dashboard response shapes — PO Spec §4 (2026-06-21) +
// post-M3 review (Amber aggregate tile + Red-flag inclusion).
//
// Shared between API (`apps/api/src/routes/hr.ts`) and SPA
// (`apps/web/src/routes/HrDashboard.tsx`) so the contract is
// single-sourced. The Zod schema is the runtime guard; the inferred
// TS type is the compile-time backstop. Privacy posture (DESIGN §6.4):
// the aggregate surfaces are *counts only*, never per-case attribution,
// and the min-pool floor collapses sub-threshold categories into a
// single boolean note rather than naming them (a named sub-threshold
// category is itself a positive signal that ≥1 event occurred).

import { z } from 'zod';
import { CLASSIFIER_CATEGORIES } from './maeve.ts';

const ClassifierCategorySchema = z.enum(CLASSIFIER_CATEGORIES);

export const WellbeingCategoryCountSchema = z.object({
  category: ClassifierCategorySchema,
  count: z.number().int().nonnegative(),
});
export type WellbeingCategoryCount = z.infer<typeof WellbeingCategoryCountSchema>;

// One block of the dashboard aggregate — Amber and Red share the
// shape; they differ in their min-pool floor and suppression copy.
// `suppressed` flags that no per-category line was emitted for the
// whole block (every category was below the floor) — the UI uses it
// to render a single "pool too small" note instead of a list.
export const WellbeingAggregateBlockSchema = z.object({
  // True if no per-category line was emitted (either because all
  // categories are below the floor, or because there were zero events
  // in the window). The UI renders suppression copy in this case.
  suppressed: z.boolean(),
  // Total events in the block over the trailing window. Always
  // exposed (no PII; it's the headline count on the tile). When
  // `suppressed: true`, the UI may use this with the suppression
  // copy — the total is a count, not a per-case attribution.
  total: z.number().int().nonnegative(),
  // The configured min-pool for the block. UI echoes this in the
  // "suppressed because pool < N" copy so the operator knows the
  // threshold.
  threshold: z.number().int().positive(),
  // Surfaced (above-floor) categories, only present when
  // `suppressed: false`.
  categories: z.array(WellbeingCategoryCountSchema),
});
export type WellbeingAggregateBlock = z.infer<typeof WellbeingAggregateBlockSchema>;

// The dashboard's `amberAggregate` field — both Amber and Red
// surface-side data, sharing the same shape. The dashboard endpoint
// attaches one of these per block.
export const AmberAggregateSchema = z.object({
  // Amber aggregate — per-org overridable floor (default 5).
  amber: WellbeingAggregateBlockSchema,
  // Red aggregate — hardcoded floor (10). The PO chose 10 to
  // mitigate the triangulation risk (named email + Distress_Red
  // flag + count = same person in a small team). CGC review still
  // required for production sign-off.
  red: WellbeingAggregateBlockSchema,
});
export type AmberAggregate = z.infer<typeof AmberAggregateSchema>;

// Display-side humaniser for the snake_case `ClassifierCategory` enum
// values. Lives in the shared module rather than the React component
// so the HR-Maeve LLM-context composer, the CGC sign-off block, and
// the dashboard tile all resolve the same display string for a given
// category — they cannot drift.
//
// The transformation is a one-liner today, but it's load-bearing for
// §6.4: a category name that renders differently on the dashboard vs.
// in a quoted CGC review would be a privacy audit-trail inconsistency.
export function humaniseClassifierCategory(category: string): string {
  return category
    .split('_')
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Engagement aggregate — PO Spec Phase 4 §3 (2026-06-21).
//
// Distinct from the Wellbeing aggregate (Amber + Red): this surface
// tracks benign engagement signals (support-space activation, resource
// access) rather than safety signals. The privacy posture is the same
// shape — counts only, k-anonymity floor — but the floor is a single
// hardcoded `K_ANON_MIN_CASES` (matching the per-manager breakdown
// floor at the dashboard's H7 review) rather than the per-org
// overridable Amber pool.
//
// Why a separate block instead of folding into `AmberAggregate`:
//   - Different privacy floors (Amber = per-org overridable, Engagement
//     = hardcoded 3 — small-team re-identification risk is the same
//     as the per-manager breakdown's risk).
//   - Different suppression copy ("suppressed because pool below N"
//     vs. "no engagement in the last 90 days") — the operator-actionable
//     reading differs.
//   - Different update cadence: the engagement counters are computed
//     off the `bereavement_cases` + `resource_access_events` tables on
//     every dashboard read; the Amber/Red aggregate is computed off
//     the rolling 90-day incident log window. Conflating them would
//     tie two unrelated windows together.
// ---------------------------------------------------------------------------

// One block of the engagement aggregate — `supportSpaceActivated`
// and `resourcesAccessed` share the shape. `suppressed` flags that
// the open-case pool is below the k-anon floor (every aggregation
// suppressed when N < threshold). The UI uses it to render a single
// "pool too small" note instead of a count.
export const EngagementAggregateBlockSchema = z.object({
  // True if no per-case count was emitted because the open-case pool
  // is below the k-anon floor. The UI renders suppression copy in
  // this case — same pattern as the Amber/Red blocks.
  suppressed: z.boolean(),
  // Total events in the block across the org's open cases. Always
  // exposed (no PII; it's the headline count on the tile). When
  // `suppressed: true`, the UI may use this with the suppression
  // copy — the total is a count, not a per-case attribution.
  total: z.number().int().nonnegative(),
  // Total open cases in the org (denominator). Used by the UI
  // echo in the suppression copy ("3 of 7 open cases…").
  totalCases: z.number().int().nonnegative(),
  // The k-anon floor — the threshold below which the block is
  // suppressed. Hardcoded `K_ANON_MIN_CASES` (mirrors the per-manager
  // breakdown floor at hr.ts:152). Surfaced so the operator knows
  // the privacy threshold without reading the source.
  threshold: z.number().int().positive(),
});
export type EngagementAggregateBlock = z.infer<typeof EngagementAggregateBlockSchema>;

// The dashboard's `engagementAggregate` field — both Support Space
// Activated and Resources Accessed surface-side data, sharing the
// same shape.
export const EngagementAggregateSchema = z.object({
  supportSpaceActivated: EngagementAggregateBlockSchema,
  resourcesAccessed: EngagementAggregateBlockSchema,
});
export type EngagementAggregate = z.infer<typeof EngagementAggregateSchema>;

// ---------------------------------------------------------------------------
// Support-preferences aggregate — Vision & Strategy §5/§6.
//
// Org-wide, counts-only view of how employees have set their support
// preferences (contact method, touch-point opt-in, cadence). NEVER an
// individual's choice: the per-option breakdown is suppressed below the
// k-anon floor (`total < threshold`), where `total` is the number of
// employees who have *set* preferences. When `suppressed`, the route zeroes
// the breakdown so a tiny pool can't be re-identified. The headline `total`
// is always safe (a bare count, no attribution). "Structure without
// surveillance" — Vision & Strategy §12.
// ---------------------------------------------------------------------------
export const PreferencesAggregateSchema = z.object({
  suppressed: z.boolean(),
  total: z.number().int().nonnegative(),
  threshold: z.number().int().positive(),
  contact: z.object({
    email: z.number().int().nonnegative(),
    workspace: z.number().int().nonnegative(),
    none: z.number().int().nonnegative(),
  }),
  touchpoints: z.object({
    optIn: z.number().int().nonnegative(),
    optOut: z.number().int().nonnegative(),
  }),
  cadence: z.object({
    default: z.number().int().nonnegative(),
    lighter: z.number().int().nonnegative(),
    none: z.number().int().nonnegative(),
  }),
});
export type PreferencesAggregate = z.infer<typeof PreferencesAggregateSchema>;

// ---------------------------------------------------------------------------
// Per-manager touch point adherence — Phase 4 §3 ("monitor manager performance
// and adherence to the support framework").
//
// Definition (single-sourced here so the repo aggregation, the route, and the
// UI label all agree): of the touchpoints that have *come due* for a manager's
// non-Closed cases, what fraction did the manager action (mark
// `Completed`/`Done_Informally`) rather than leave un-actioned past the 48h
// overdue grace. A touch point is *evaluable* once it is either actioned OR past
// the grace (`scheduledFor < now - OVERDUE_TOUCHPOINT_THRESHOLD_MS`) — the same
// grace the `Overdue_Touchpoint` flag and the consecutive-miss run use, so the
// "missed" definition can't drift across surfaces. Not-yet-due touch points are
// excluded (they aren't a performance signal yet).
//
//   adherence = met / evaluable     (null when evaluable === 0)
//
// Privacy: adherence is per-manager performance data, so it rides the SAME
// k-anonymity floor as the per-manager load breakdown — it is only ever
// attached to managers with ≥ K_ANON_MIN_CASES open cases (the route never
// computes it for sub-floor managers or the "Other managers" rollup, where a
// single sub-floor manager could be de-anonymised by their own rate).
export const ManagerAdherenceSchema = z.object({
  // Count of evaluable touch points the manager actioned (Completed /
  // Done_Informally).
  met: z.number().int().nonnegative(),
  // Count of touch points that have come due (actioned, or past the 48h grace).
  // Always ≥ 1 when this object is present — `null` is used instead of a
  // zero-denominator object so the UI never divides by zero.
  evaluable: z.number().int().positive(),
  // Pre-computed met/evaluable in [0, 1]. Computed server-side so the tile is
  // a pure presentation layer (same posture as the aggregate blocks above).
  rate: z.number().min(0).max(1),
});
export type ManagerAdherence = z.infer<typeof ManagerAdherenceSchema>;

// One row of the per-manager load breakdown. Single-sourced so the route and
// the SPA share the contract. `adherence` is null when the manager has no
// touch point that has come due yet (e.g. all their cases are brand new), and is
// always null on the synthetic "Other managers" (`__other__`) rollup row.
export const ManagerLoadRowSchema = z.object({
  managerId: z.string(),
  managerName: z.string(),
  onTrack: z.number().int().nonnegative(),
  dueSoon: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  adherence: ManagerAdherenceSchema.nullable(),
});
export type ManagerLoadRow = z.infer<typeof ManagerLoadRowSchema>;

// Support Framework spec §9 — org-level framework oversight metrics. Operational
// (workload + adherence + routing), never judgemental. `lossTypeBreakdown` is
// k-anon-gated by the route: a loss type below the floor is rolled into "Other"
// so a small org can't re-identify a sensitive loss. `completion` is the
// framework-adherence rate (met of evaluable touch points), org-wide.
export const FrameworkLossTypeCountSchema = z.object({
  label: z.string(),
  count: z.number().int().nonnegative(),
});

// PO follow-up ("doesn't show the 7 step support pathway") — the framework's
// own stages, not just flat workload numbers. Mirrors `SUPPORT_FRAMEWORK_STAGES`
// in `./cases.ts` (name/anchor per stage — single source of truth for the
// stage list itself) with per-stage touch-point counts across the org's Open
// cases. Same universe as `touchPointsDueThisWeek`/`overdueTouchPoints` above
// (materialised touch points only) and the same privacy posture as the rest
// of `frameworkMetrics` — org-wide counts, no per-employee attribution, so
// unlike `lossTypeBreakdown` this is NOT k-anon-gated.
export const FrameworkStageCountSchema = z.object({
  stage: z.number().int().positive(),
  name: z.string(),
  anchor: z.enum(['loss', 'rtw']),
  scheduled: z.number().int().nonnegative(),
  dueSoon: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
});
export type FrameworkStageCount = z.infer<typeof FrameworkStageCountSchema>;

export const FrameworkMetricsSchema = z.object({
  touchPointsDueThisWeek: z.number().int().nonnegative(),
  overdueTouchPoints: z.number().int().nonnegative(),
  completion: z.object({
    met: z.number().int().nonnegative(),
    evaluable: z.number().int().nonnegative(),
  }),
  pausedPathways: z.number().int().nonnegative(),
  lossTypeBreakdown: z.array(FrameworkLossTypeCountSchema),
  stageBreakdown: z.array(FrameworkStageCountSchema),
});
export type FrameworkMetrics = z.infer<typeof FrameworkMetricsSchema>;
